import { useEffect, useState } from "react";
import { Loader2, MessageCircleHeart, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { api, errMessage } from "../../lib/api";

// Weekly math digest on the parent dashboard: mastery summary + LLM-crafted
// dinner-table conversation prompts per child. A background job compiles one
// per child per week; parents can also generate on demand.
export const MathDigest = () => {
  const [entitled, setEntitled] = useState(false);
  const [digests, setDigests] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/math/digests").then(({ data }) => setDigests(data.digests)).catch(() => setDigests([]));

  useEffect(() => {
    api.get("/math/access")
      .then(({ data }) => { setEntitled(Boolean(data.math_entitled)); if (data.math_entitled) load(); })
      .catch(() => {});
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/math/digests/generate");
      if (!data.digests.length) toast("No math practice this week yet — play first, then generate!");
      await load();
    } catch (e) { toast.error(errMessage(e)); }
    setBusy(false);
  };

  if (!entitled || digests === null) return null;
  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-4"
      data-testid="math-digest-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-sm font-bold font-display flex items-center gap-2">
          <MessageCircleHeart className="h-4 w-4 text-secondary" aria-hidden="true" />
          Weekly math digest
        </h3>
        <Button size="sm" variant="outline" className="ml-auto rounded-full gap-1 text-xs"
          disabled={busy} onClick={generate} data-testid="digest-generate">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                : <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />}
          Generate this week's
        </Button>
      </div>
      {digests.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="digest-empty">
          After a week of math practice, a summary and dinner-table conversation
          starters will appear here (and be ready for email).
        </p>
      ) : (
        <div className="space-y-4">
          {digests.map((d) => (
            <div key={d.digest_id} className="rounded-xl border border-border p-4 space-y-2"
              data-testid={`digest-card-${d.child_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-bold">{d.child_name}</p>
                <span className="text-xs text-muted-foreground">
                  week of {d.week_start} · {d.summary.solved}/{d.summary.attempts} solved
                </span>
              </div>
              {(d.summary.hub_levels > 0 || d.summary.hub_mastered?.length > 0) && (
                <p className="text-xs font-bold text-cyan-700" data-testid={`digest-hub-${d.child_id}`}>
                  Quest map: {d.summary.hub_levels || 0} level{d.summary.hub_levels === 1 ? "" : "s"} cleared
                  {d.summary.hub_mastered?.length > 0 && <> · Mastered: {d.summary.hub_mastered.join(", ")}</>}
                </p>
              )}
              {d.summary.mastered.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {d.summary.mastered.map((c) => (
                    <span key={c} className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-bold px-2 py-0.5">
                      {c}
                    </span>
                  ))}
                </div>
              )}
              <ul className="space-y-1.5" data-testid={`digest-prompts-${d.child_id}`}>
                {d.prompts.map((p, i) => (
                  <li key={i} className="text-xs rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 italic">
                    "{p}"
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
